import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LOGIN_PATH, USER_ROLE_PATH } from '@constants/routes';
import { useAppDispatch } from '@store/hooks';
import { setFromOnboarding } from '@store/reducers/auth/signupSlice';

const slides = [
    {
        image: "/public/images/onboarding_left.png",
        line1: "Intelligent infusion. Safer care.",
        line2: "Better outcomes."
    },
    {
        image: "/public/images/onboarding_left_2.png",
        line1: "Smarter alerts. Better care.",
        line2: "Always one step ahead."
    },
    {
        image: "/public/images/onboarding_left_3.png",
        line1: "Smarter infusion. Safer care.",
        line2: "Better outcomes."
    }
];

const OnboardingPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const handelLogin = () => {
        dispatch(setFromOnboarding(false));
        navigate(LOGIN_PATH);
    };

    const handelSignup = () => {
        dispatch(setFromOnboarding(true));
        navigate(USER_ROLE_PATH);
    };

    return (
        <div className="w-full h-screen min-h-screen flex flex-col lg:flex-row bg-white overflow-hidden font-sans">
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(4px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in {
                    animation: fadeIn 0.4s ease-out forwards;
                }
            `}</style>
            
            {/* Left Column: Curved illustration panel */}
            <div className="hidden lg:flex lg:w-[52%] bg-white relative overflow-hidden h-full">
                {slides.map((slide, index) => (
                    <img
                        key={index}
                        src={slide.image}
                        className={`absolute inset-0 w-full h-full object-contain object-right select-none pointer-events-none transition-opacity duration-700 ease-in-out ${
                            currentSlide === index ? "opacity-100 z-10" : "opacity-0 z-0"
                        }`}
                        alt="Dripset Patient Infusion Monitoring"
                    />
                ))}
            </div>

            {/* Right Column: Interaction form & Branding */}
            <div className="w-full lg:w-[48%] flex flex-col justify-between relative bg-[#f8fafc] px-8 py-8 md:px-16 xl:px-20 z-10 h-full">
                {/* Top Row: Mobile Logo */}
                <div className="flex h-12 items-center justify-start w-full">
                    {/* Mobile Logo (visible only on small screens) */}
                    <div className="lg:hidden flex items-center">
                        <img 
                            src="/public/images/emsense_logo.png" 
                            className="h-10 object-contain" 
                            alt="Emsense Logo" 
                        />
                    </div>
                </div>

                {/* Middle Row: Content & Interactive Buttons */}
                <div className="flex-grow flex flex-col justify-center items-center text-center max-w-sm mx-auto w-full py-6 z-10">
                    {/* Large Dripset Logo */}
                    <img
                        src="/public/images/logo.png"
                        alt="Dripset Logo"
                        className="h-16 object-contain mb-6 select-none pointer-events-none"
                    />

                    {/* Subtitle with transition */}
                    <div className="h-[52px] mb-8 flex items-center justify-center overflow-hidden">
                        <p 
                            key={currentSlide}
                            className="text-slate-700 text-sm md:text-base font-semibold leading-relaxed max-w-[280px] animate-fade-in"
                        >
                            {slides[currentSlide].line1}<br />
                            {slides[currentSlide].line2}
                        </p>
                    </div>

                    {/* Create Account Button */}
                    <button
                        className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 mb-3.5 text-sm md:text-base tracking-wide"
                        onClick={handelSignup}
                    >
                        {/* Person-Plus SVG Icon */}
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M19 8v6M22 11h-6" />
                        </svg>
                        <span>{t('auth.create_account')}</span>
                    </button>

                    {/* Login Button */}
                    <button
                        className="w-full flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-blue-600 font-semibold py-3 px-6 rounded-xl border border-blue-200 hover:border-blue-300 transition-all duration-200 text-sm md:text-base tracking-wide shadow-sm mb-8"
                        onClick={handelLogin}
                    >
                        {/* Log-In SVG Icon */}
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                            <path d="M15 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8M10 17l5-5-5-5M15 12H3" />
                        </svg>
                        <span>{t('auth.login_btn')}</span>
                    </button>

                    {/* Slide indicator dots (positioned at the bottom center of the interactive block) */}
                    <div className="flex items-center justify-center gap-2 select-none">
                        {slides.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                className={`h-2.5 rounded-full transition-all duration-300 ${
                                    currentSlide === index 
                                        ? "w-8 bg-blue-600 shadow-sm" 
                                        : "w-2.5 bg-slate-200 hover:bg-slate-300"
                                }`}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>

                {/* Bottom Row: Mobile Footer Trademark */}
                <div className="flex justify-center lg:justify-start w-full text-[10px] text-slate-400 z-10 select-none">
                    <span className="lg:hidden">© 2026 EMSENSE Inc. All rights reserved.</span>
                </div>

                {/* Decorative Bottom Right Vector Waves */}
                <svg
                    className="absolute bottom-0 right-0 w-[240px] h-[150px] pointer-events-none z-0 opacity-60"
                    viewBox="0 0 240 150"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M 0 150 Q 60 110 120 120 T 240 85" stroke="#e2e8f0" strokeWidth="1" fill="none" />
                    <path d="M 0 140 Q 70 100 130 110 T 240 75" stroke="#dbeafe" strokeWidth="1.2" opacity="0.5" fill="none" />
                    <path d="M 0 130 Q 80 90 140 100 T 240 65" stroke="#bfdbfe" strokeWidth="1.2" opacity="0.6" fill="none" />
                    <path d="M 0 120 Q 90 80 150 90 T 240 55" stroke="#93c5fd" strokeWidth="1.2" opacity="0.4" fill="none" />
                    <path d="M 0 110 Q 120 70 160 80 T 240 45" stroke="#60a5fa" strokeWidth="1" opacity="0.2" fill="none" />
                </svg>

                {/* Emsense Logo at Right Bottom Corner */}
                <div className="absolute bottom-4 right-4 z-20">
                    <img 
                        src="/public/images/emsense_logo.png" 
                        alt="Emsense Logo" 
                        className="h-10 object-contain select-none pointer-events-none" 
                    />
                </div>
            </div>
        </div>
    );
};

export default OnboardingPage;
